/**
 * Design Tokens System
 * Système de design centralisé inspiré du BackOffice
 * Tous les tokens sont typés et réutilisables
 */

// ============================================================================
// COLORS
// ============================================================================

export const COLORS = {
  // Slate (New Premium Gray - Blueish)
  slate: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
    950: '#020617',
  },

  // Grayscale (Maintenant alias de Slate pour cohérence globale)
  gray: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },

  // Success (vert)
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },

  // Warning (orange)
  warning: {
    50: '#fff7ed',
    100: '#ffedd5',
    200: '#fed7aa',
    300: '#fdba74',
    400: '#fb923c',
    500: '#f97316',
    600: '#ea580c',
    700: '#c2410c',
    800: '#9a3412',
    900: '#7c2d12',
  },

  // Error (rouge)
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },

  // Info (bleu)
  info: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
} as const;

// ============================================================================
// RADIUS
// ============================================================================

export const RADIUS = {
  card: '24px',
  button: '16px',
  input: '12px',
  badge: '9999px',
  modal: '24px',
  tab: '16px',      // Pour les onglets (Dashboard, Data, Budget...)
  menu: '16px',     // Pour les menus et dropdowns
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  '2xl': '32px',
  full: '9999px',
} as const;

// ============================================================================
// SHADOWS
// ============================================================================

export const SHADOW = {
  subtle: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  elevated: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  float: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  card: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  cardHover: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  glass: '0 8px 32px 0 rgba(31, 38, 135, 0.1)',
  glassHover: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
  glassHeavy: '0 10px 30px rgba(15, 23, 42, 0.08)',
  none: 'none',
} as const;

// ============================================================================
// MOTION (Pour Framer Motion)
// ============================================================================

export const MOTION = {
  duration: {
    instant: 0.1,
    fast: 0.2,
    normal: 0.3,
    slow: 0.5,
    slower: 0.7,
  },
  easing: {
    ios: [0.4, 0.0, 0.2, 1.0],
    material: [0.4, 0.0, 0.6, 1.0],
    easeIn: [0.4, 0.0, 1.0, 1.0],
    easeOut: [0.0, 0.0, 0.2, 1.0],
    bounce: [0.68, -0.55, 0.265, 1.55],
  },
  distance: {
    minimal: 4,
    light: 12,
    normal: 20,
    large: 40,
    xlarge: 80,
  },
  scale: {
    hoverMinimal: 1.02,
    hoverSubtle: 1.05,
    hoverNormal: 1.1,
    tap: 0.95,
    press: 0.98,
  },
  stagger: {
    fast: 0.05,
    normal: 0.1,
    slow: 0.15,
  },
} as const;

// ============================================================================
// CHART COLORS
// ============================================================================

/**
 * Palette catégorielle — 16 teintes dans un ordre fixe.
 *
 * Les palettes précédentes ne comptaient que 3 ou 4 couleurs, recyclées via
 * `index % length` : au 5e élément, deux séries recevaient la même couleur
 * (le cas signalé sur un projet à 5 activations).
 *
 * Ordre validé avec le validateur d'accessibilité data-viz (fond clair
 * #fcfcfb) — bande de luminosité, saturation minimale, séparation daltonisme
 * et vision normale : tout passe. Pire paire adjacente : ΔE 8.7 en deutéranopie
 * (seuil 8), ΔE 18.3 en vision normale (seuil 15).
 *
 * DEUX RÈGLES À RESPECTER EN L'UTILISANT :
 * 1. L'ordre est fixe. Ne pas réordonner sans repasser le validateur : les
 *    contrôles portent sur les paires ADJACENTES, donc l'ordre est le calcul.
 * 2. Trois teintes (#1baf7a, #eda100, #e87ba4) sont sous 3:1 de contraste sur
 *    fond clair. Elles ne sont utilisables qu'accompagnées d'une étiquette
 *    lisible (pourcentage sur le segment, nom en légende) — jamais la couleur
 *    comme seul indice.
 *
 * Au-delà de 16 éléments le recyclage reprend, faute de teintes distinguables.
 * C'est un choix assumé : à ce niveau de découpage, un camembert n'est de toute
 * façon plus lisible et il vaudrait mieux regrouper les plus petites parts.
 */
export const CATEGORICAL_PALETTE = [
  '#2a78d6', // 1  bleu
  '#eb6834', // 2  orange
  '#1baf7a', // 3  aqua
  '#eda100', // 4  jaune
  '#e87ba4', // 5  magenta
  '#008300', // 6  vert
  '#4a3aa7', // 7  violet
  '#e34948', // 8  rouge
  '#0a9fbf', // 9  cyan
  '#a1662f', // 10 brun
  '#6ea100', // 11 lime
  '#9b3fb5', // 12 pourpre
  '#00a0a0', // 13 turquoise
  '#c2185b', // 14 framboise
  '#7a6bd6', // 15 bleu-violet
  '#7d8b00', // 16 olive
] as const;

/**
 * Couleur d'une série à partir de son rang.
 *
 * Le rang doit être STABLE dans le projet (position de l'activation ou du type
 * de dépense dans la liste complète), pas la position dans le graphique
 * courant : sinon un filtre qui masque un élément repeint tous les autres.
 */
export function getCategoricalColor(index: number): string {
  return CATEGORICAL_PALETTE[index % CATEGORICAL_PALETTE.length];
}

export const CHART_COLORS = {
  primary: CATEGORICAL_PALETTE,
  categorical: CATEGORICAL_PALETTE,
  donut: CATEGORICAL_PALETTE,
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get color by semantic type
 */
export function getSemanticColor(
  type: 'success' | 'warning' | 'error' | 'info',
  shade: keyof typeof COLORS.success = 500
): string {
  return COLORS[type][shade];
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export const DESIGN_TOKENS = {
  colors: COLORS,
  radius: RADIUS,
  shadow: SHADOW,
  motion: MOTION,
  chartColors: CHART_COLORS,
} as const;

export default DESIGN_TOKENS;
